import type {
  AlertConditionConfig,
  AlertConditionMatchMode,
} from "../../../services/delivery/alertCondition.types";
import type { AudienceApi } from "../../../services/distributions/distributions.types";
import type { EndsMode } from "../../../utils/storyRefreshTypes";

export type {
  AlertConditionConfig,
  AlertConditionMatchMode,
  AlertConditionMode,
} from "../../../services/delivery/alertCondition.types";

export type Platform = "slack" | "email" | "teams";

export type DeliveryType = "scheduled" | "alert" | "immediate";

export type DeliveryArtifact = "story" | "memo" | "presentation";

export type DeliveryFormat = "presentation" | "pdf" | "video" | "image-set";

export type DeliveryStatus = "active" | "cancelled" | "completed" | "paused";

export type WhenToSend = "send-now" | "schedule" | "alert-on-condition";

export type ScheduleUnit = "day" | "week" | "month";

export type Weekday = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

export const WEEKDAYS: readonly Weekday[] = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;

export interface ScheduleConfig {
  every: number;
  unit: ScheduleUnit;
  /** Selected weekdays — only relevant when unit is "week". */
  repeatOn: Weekday[];
  /** 24h hour value 0-23. Null means not selected. */
  refreshHour: number | null;
  /** IANA-like tz identifier (we keep a string so presentation layer controls the list). */
  timezone: string | null;
  /** ISO yyyy-mm-dd. Empty means not selected. */
  startsOn: string;
  /** End condition for the recurring delivery. */
  endsMode: EndsMode;
  /** ISO yyyy-mm-dd. Required when `endsMode` is "on". */
  endsOn: string;
  /** Occurrence count. Required when `endsMode` is "after". */
  endsAfter: number | null;
}

export interface Delivery {
  id: string;
  platform: Platform;
  destinations: string[];
  hiddenDestinationCount?: number;
  deliveryType: DeliveryType;
  format: DeliveryFormat;
  status: DeliveryStatus;
  /** False when the form can't show it (a Send now, or a schedule it has no fields for). */
  editable?: boolean;
}

export interface SendNowFormState {
  platform: Platform;
  destinations: string[];
  whenToSend: WhenToSend;
  artifact: DeliveryArtifact;
  format: DeliveryFormat;
  /** Presentation theme key for a rendered deck (see deliveryTheme.ts);
   *  null means the default theme. */
  theme: string | null;
  /** Alert-on-condition only for now: whether the rendered artifact rides
   * along with the alert message. Defaults true (opt-out, not opt-in) —
   * see StoryAlertBase.include_attachment. */
  includeAttachment: boolean;
  /** Who receives the copies: inside = each member at their own level, outside = one copy for everyone. */
  audience: AudienceApi;
  message: string;
  schedule: ScheduleConfig;
  /** Shown when `whenToSend` is "alert-on-condition" only. */
  alertConditions: AlertConditionConfig[];
  /** Applied when more than one alert condition is present. */
  conditionMatchMode: AlertConditionMatchMode;
}

/**
 * The preview card's attachment when it's the real rendered deck (see
 * deliveryAttachmentPreview.ts). Absent → a neutral placeholder named from the format.
 */
export interface DeliveryAttachmentPreview {
  status: "rendering" | "error" | "ready";
  /** Front page of the rendered file (its first preview page). */
  imageUrl: string | undefined;
  /** File type as sent, e.g. "PowerPoint (.pptx)". */
  name: string;
  /** e.g. "107 KB"; null until known. */
  sizeLabel: string | null;
  /** Backend's reason when the render failed. */
  errorMessage: string | null;
}

export interface DestinationOption {
  value: string;
  label: string;
  kind: "channel" | "user" | "team";
}
